import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { Big } = Util;
import Slider from "@/components/Pages/Trade/_cmpt/form/_cmpt/slider";
import styles from "./index.module.scss";

interface Props {
  value: string; // 当前杠杆值
  min: number; // 最小杠杆
  max: number; // 最大杠杆
  step?: number; // 步进值，默认 5
  disabled?: boolean;
  onChange?: (value: string) => void;
  className?: string;
  width?: string | number; // 宽度，可以是字符串（如 "136px"）或数字（如 136，会转换为 px）
}

const LeverageSelector: React.FC<Props> = ({ value, min, max, step = 5, disabled = false, onChange, className, width }) => {
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState<string>(value); // 临时值，只有确定后才更新

  // 计算宽度样式
  const widthStyle = useMemo(() => {
    if (width === undefined) return {};
    if (typeof width === "number") {
      return { width: `${width}px` };
    }
    return { width };
  }, [width]);

  // 当前杠杆数值（实际值）
  const leverageNum = useMemo(() => {
    const num = Number(value || min);
    return Math.max(min, Math.min(max, isNaN(num) ? min : num));
  }, [value, min, max]);

  // 临时杠杆数值（用于弹窗内调整）
  const tempLeverageNum = useMemo(() => {
    const num = Number(tempValue || min);
    return Math.max(min, Math.min(max, isNaN(num) ? min : num));
  }, [tempValue, min, max]);

  // 按步进对齐到 [min, max]（与加减、滑块一致）
  const snapToStep = useCallback(
    (num: number) => {
      const snapped = min + Math.round((num - min) / step) * step;
      return Math.max(min, Math.min(max, snapped));
    },
    [min, max, step]
  );

  // 打开弹窗时，初始化临时值并对齐步进
  useEffect(() => {
    if (isOpen) {
      const num = Number(value || min);
      const base = isNaN(num) ? min : num;
      setTempValue(String(snapToStep(base)));
    }
  }, [isOpen, value, min, snapToStep]);


  // 处理增加（临时值）
  const handleIncrease = useCallback(() => {
    if (disabled) return;
    const newValue = Math.min(max, tempLeverageNum + step);
    setTempValue(String(newValue));
  }, [disabled, tempLeverageNum, step, max]);

  // 处理减少（临时值）
  const handleDecrease = useCallback(() => {
    if (disabled) return;
    const newValue = Math.max(min, tempLeverageNum - step);
    setTempValue(String(newValue));
  }, [disabled, tempLeverageNum, step, min]);

  // 处理滑块变化（临时值）
  const sliderValue = useMemo(() => {
    return String(tempLeverageNum);
  }, [tempLeverageNum]);

  const sliderMax = useMemo(() => {
    return String(max);
  }, [max]);

  const handleSliderValueChange = useCallback(
    (val: string) => {
      const num = Number(val);
      if (!isNaN(num)) {
        setTempValue(String(snapToStep(num)));
      }
    },
    [snapToStep]
  );

  // 确定按钮
  const handleConfirm = useCallback(() => {
    onChange && onChange(String(tempLeverageNum));
    setIsOpen(false);
  }, [tempLeverageNum, onChange]);

  // 取消/关闭
  const handleClose = useCallback(() => {
    setTempValue(value); // 重置临时值
    setIsOpen(false);
  }, [value]);

  // 阻止背景滚动
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  return (
    <>
      {/* 触发按钮 */}
      <div className={cx(styles.leverageSelector, className)} style={widthStyle}>
        <div className={styles.trigger} onClick={() => !disabled && setIsOpen(true)}>
          <span className={styles.triggerValue}>{leverageNum}X</span>
          <svg
            className={styles.triggerArrow}
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="6"
            viewBox="0 0 10 6"
            fill="none"
          >
            <path d="M10 0H0L5 6L10 0Z" fill="rgba(255, 255, 255, 0.56)" />
          </svg>
        </div>
      </div>

      {/* 全屏弹窗 */}
      {isOpen && (
        <div className={styles.modal}>
          {/* 蒙层 */}
          <div className={styles.overlay} onClick={handleClose} />

          {/* 弹窗内容 */}
          <div className={styles.modalContent}>
            {/* 头部 */}
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>{t("cfd.leverageAdjustment")}</span>
              <button className={styles.modalClose} onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="rgba(255, 255, 255, 0.6)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* 当前杠杆显示 */}
            <div className={styles.currentLeverage}>
              <span className={styles.currentLabel}>{t("cfd.current")}</span>
              <div className={styles.currentValue}>
                <span className={styles.valueNumber}>{leverageNum}</span>
                <span className={styles.valueUnit}>X</span>
              </div>
            </div>

            {/* 内容区域 */}
            <div className={styles.modalBody}>
              {/* 输入框和加减按钮 */}
              <div className={styles.inputGroup}>
                <button
                  className={cx(styles.stepButton, styles.stepButtonDecrease)}
                  onClick={handleDecrease}
                  disabled={disabled || tempLeverageNum <= min}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div className={styles.inputWrapper}>
                  <span className={styles.inputValue}>{tempLeverageNum}</span>
                  <span className={styles.inputSuffix}>X</span>
                </div>
                <button
                  className={cx(styles.stepButton, styles.stepButtonIncrease)}
                  onClick={handleIncrease}
                  disabled={disabled || tempLeverageNum >= max}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10 5V15M5 10H15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>

              {/* 滑块 */}
              <div className={styles.sliderGroup}>
                <div className={styles.sliderLabels}>
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <div key={index} className={styles.sliderLabel}></div>
                  ))}
                </div>
                <div className={styles.sliderWrapper}>
                  <Slider
                    className={styles.slider}
                    value={sliderValue}
                    max={sliderMax}
                    point={0}
                    disabled={disabled}
                    onChange={handleSliderValueChange}
                  />
                </div>
              </div>
            </div>

            {/* 确定按钮 */}
            <div className={styles.modalFooter}>
              <button className={styles.confirmButton} onClick={handleConfirm} disabled={disabled}>
                {t("cfd.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeverageSelector;
